import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import DemoRequestModal from '../../components/marketing/DemoRequestModal';
import { pricingTiers, pricingFAQ, type PricingTier } from '../../data/marketing/pricing';
import { trackEvent } from '../../lib/analytics';
import { useAuth } from '../../contexts/AuthContext';
import {
  useStartTrial,
  type TrialEligibleTier,
} from '../../hooks/api/useStartTrial';
import { useToast } from '../../components/ui/ToastProvider';
import { extractApiErrorMessage } from '../../utils/apiError';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const TRIAL_ELIGIBLE_TIERS: ReadonlyArray<TrialEligibleTier> = [
  'sprout',
];

const isTrialEligibleTier = (tier: PricingTier['tier']): tier is TrialEligibleTier =>
  (TRIAL_ELIGIBLE_TIERS as ReadonlyArray<string>).includes(tier);

const PricingPage: React.FC = () => {
  const [annual, setAnnual] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const startTrial = useStartTrial();
  const toast = useToast();

  const handleTrialClick = (tier: PricingTier) => {
    if (!isTrialEligibleTier(tier.tier)) return;

    // Unauthenticated users — send them to log in. Auto-resume of the trial
    // flow after login is a future polish, intentionally out of scope here.
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (startTrial.isPending) return;

    startTrial.mutate(tier.tier, {
      onSuccess: () => {
        trackEvent('pricing_trial_started', { tier_name: tier.name });
        toast.success(
          `14-day ${tier.name} trial started!`,
          'Welcome aboard.'
        );
        navigate('/dashboard');
      },
      onError: (err) => {
        toast.error(
          'Could not start trial',
          extractApiErrorMessage(err, 'Please try again or contact support.')
        );
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Pricing — Sprout Educational Platform</title>
        <meta name="description" content="Affordable plans for South African schools from free to enterprise. Start with Seedling (free) or grow with Sapling, Sprout, or Forest plans." />
        <meta property="og:title" content="Pricing — Sprout Educational Platform" />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/pricing" />
      </Helmet>

      {/* Header */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-display font-bold text-gray-900 dark:text-white"
          >
            Simple, transparent pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-gray-600 dark:text-gray-300"
          >
            Start free, upgrade as you grow. All prices in South African Rand.
          </motion.p>

          {/* Toggle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1"
          >
            <button
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                !annual ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => {
                trackEvent('pricing_billing_toggled', { billing: 'monthly' });
                setAnnual(false);
              }}
            >
              Monthly
            </button>
            <button
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                annual ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => {
                trackEvent('pricing_billing_toggled', { billing: 'annual' });
                setAnnual(true);
              }}
            >
              Annual <span className="text-secondary-600 text-xs ml-1">Save 10%</span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 -mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {pricingTiers.map((tier, i) => {
              const isPaid = tier.tier !== 'seedling';

              // Computed display price + per-child suffix for the chosen billing cycle.
              let priceLabel: string;
              let priceSuffix: string | null = null;
              let perChildLine: string | null = null;
              if (tier.pricing.kind === 'free') {
                priceLabel = 'Free';
              } else {
                const startPrice = annual
                  ? tier.pricing.startingAnnual
                  : tier.pricing.startingMonthly;
                const perChild = annual
                  ? tier.pricing.perChildAnnual
                  : tier.pricing.perChildMonthly;
                priceLabel = `From R${startPrice.toLocaleString()}`;
                priceSuffix = `/month${annual ? ', billed annually' : ''}`;
                perChildLine = `Covers ${tier.pricing.minimumChildren} children · R${perChild}/child after that`;
              }

              return (
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card
                    className={`h-full flex flex-col ${
                      isPaid ? 'ring-2 ring-primary-500 shadow-lg relative' : ''
                    }`}
                  >
                    <div className="text-center">
                      <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">{tier.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tier.childrenRange}</p>

                      <div className="mt-4">
                        <p className="text-4xl font-display font-bold text-gray-900 dark:text-white">
                          {priceLabel}
                        </p>
                        {priceSuffix && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">{priceSuffix}</p>
                        )}
                        {perChildLine && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {perChildLine}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-4 text-center">{tier.description}</p>

                    <ul className="mt-6 space-y-2 flex-1">
                      {tier.features.map(f => (
                        <li key={f} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-secondary-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-600 dark:text-gray-300">{f}</span>
                        </li>
                      ))}
                    </ul>

                    {tier.notIncluded && tier.notIncluded.length > 0 && (
                      <ul className="mt-4 space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {tier.notIncluded.map((item) => (
                          <li key={item} className="flex items-start text-sm text-gray-400 dark:text-gray-500">
                            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-6">
                      {(() => {
                        // Track the *starting* price (R0 for free, R299/R269 for Sprout)
                        // since per-child overage isn't known until the customer commits.
                        const startingPrice =
                          tier.pricing.kind === 'free'
                            ? 0
                            : annual
                              ? tier.pricing.startingAnnual
                              : tier.pricing.startingMonthly;
                        const trackCtaClick = () =>
                          trackEvent('pricing_tier_clicked', {
                            tier_name: tier.name,
                            cta: tier.cta,
                            billing: annual ? 'annual' : 'monthly',
                            price: startingPrice,
                          });

                        // Seedling — free tier, link to login (signup not built yet)
                        if (tier.tier === 'seedling') {
                          return (
                            <Link
                              to="/login"
                              className="block"
                              onClick={trackCtaClick}
                            >
                              <Button
                                variant="outline"
                                fullWidth
                                size="sm"
                              >
                                {tier.cta}
                              </Button>
                            </Link>
                          );
                        }

                        // Sprout — trigger 14-day trial
                        const isLoading =
                          startTrial.isPending &&
                          startTrial.variables === tier.tier;
                        return (
                          <Button
                            variant="primary"
                            fullWidth
                            size="sm"
                            disabled={startTrial.isPending}
                            onClick={() => {
                              trackCtaClick();
                              handleTrialClick(tier);
                            }}
                          >
                            {isLoading ? 'Starting trial…' : tier.cta}
                          </Button>
                        );
                      })()}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Custom enterprise callout — replaces the "Forest" tier; no published
              SLA / dedicated AM / on-site training promises until we can deliver them. */}
          <div className="mt-12 max-w-2xl mx-auto text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Need something custom for a multi-site group or a non-profit with specific
              procurement requirements?{' '}
              <button
                type="button"
                onClick={() => {
                  trackEvent('pricing_custom_clicked', { location: 'pricing_below_cards' });
                  setDemoOpen(true);
                }}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium underline-offset-2 hover:underline"
              >
                Get in touch
              </button>{' '}
              and we'll see what makes sense.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-display font-bold text-gray-900 dark:text-white text-center mb-12">
            Frequently asked questions
          </motion.h2>

          <div className="space-y-3">
            {pricingFAQ.map((faq, i) => (
              <motion.div
                key={i}
                {...fadeInUp}
                className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                >
                  <span className="font-medium text-gray-900 dark:text-white">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      openFAQ === i ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DemoRequestModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
};

export default PricingPage;
